import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { History } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/rider/Nav";

function ReviewCard({ ride }) {
  const qc = useQueryClient();
  const [dispute, setDispute] = useState(null);
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/rides/${ride.id}/historical/${kind}`, kind === "dispute" ? { reason: dispute } : {});
      toast.success(kind === "approve" ? "Ride approved and added to your history" : "Dispute sent to Scoopz Admin");
      ["pastRides", "myRides", "riderRides", "myProfile"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const disputed = ride.status === "disputed";
  return (
    <div className="space-y-2 rounded-2xl border border-amber-400/40 bg-amber-400/5 p-4" data-testid={`past-ride-${ride.id}`}>
      <p className="text-xs text-muted-foreground">{when(ride.scheduled_at)} · recorded by Scoopz</p>
      <p className="text-sm font-semibold">{ride.pickup.name} → {ride.dropoff.name}</p>
      <p className="text-xs text-muted-foreground">{ride.pickup.address} → {ride.dropoff.address}</p>
      <p className="text-xs">Recorded fare {money(ride.fare)} · no charge is made by approving{ride.driver_name ? ` · Driver ${ride.driver_name}` : ""}</p>
      {ride.notes && <p className="text-xs text-muted-foreground">Note: {ride.notes}</p>}
      {disputed ? (
        <p className="text-xs font-semibold text-amber-200" data-testid={`past-ride-disputed-${ride.id}`}>Disputed: {ride.historical_review?.reason}. Scoopz Admin will review it.</p>
      ) : dispute !== null ? (
        <div className="space-y-2">
          <Textarea placeholder="What's wrong with this ride?" value={dispute} onChange={(e) => setDispute(e.target.value)} data-testid={`past-ride-reason-${ride.id}`} />
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" disabled={busy || dispute.trim().length < 2} onClick={() => act("dispute")} data-testid={`past-ride-send-dispute-${ride.id}`}>Send dispute</Button>
            <Button size="sm" variant="ghost" onClick={() => setDispute(null)}>Back</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" className="rounded-full" disabled={busy} onClick={() => act("approve")} data-testid={`past-ride-approve-${ride.id}`}>Approve ride</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDispute("")} data-testid={`past-ride-dispute-${ride.id}`}>Dispute</Button>
        </div>
      )}
    </div>
  );
}

export function PastRideReview() {
  const { data } = useQuery({ queryKey: ["pastRides"], queryFn: () => api.get("/rides/historical/mine").then((r) => r.data), refetchInterval: 30000 });
  if (!data?.length) return null;
  return (
    <section className="space-y-2" data-testid="past-ride-review">
      <SectionTitle><span className="flex items-center gap-1.5"><History className="h-3 w-3" />Past rides to confirm</span></SectionTitle>
      {data.map((r) => <ReviewCard key={r.id} ride={r} />)}
    </section>
  );
}
